import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { getLastIntrospectNodeByOperationAndParam } from '@adornis/introspect/api/get-introspect-node.js';
import { IntrospectViewer } from '@adornis/introspect/client/x-introspect-viewer.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { of } from 'rxjs';
import { addConversationComment, setAnsweringCheckedByHuman } from '../../api/clienthelper.js';
import { checkLinkAndArticleName, getRatingsByTagList, LinkAndArticleName } from '../../api/rating.js';
import { AnsweringEntity } from '../../entities/answering.js';
import { RatingEntity } from '../../entities/rating.js';

export const AnsweringDetailView = stateful(
  ({ conversationID, onClose }: { conversationID: string | null; onClose: () => void }) => {
    const [newComment, setNewComment] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
    const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [showIntrospection, setShowIntrospection] = useState<boolean>(false);

    const selectedConversation = useQuery(() => {
      if (!conversationID) return of(null);
      return getByID(
        AnsweringEntity,
        conversationID,
      )({
        _id: 1,
        odooTags: 1,
        createdAt: 1,
        comments: 1,
        answer: 1,
        query: 1,
        answeringChannelType: 1,
        expectedAnswer: 1,
        rating: 1,
        ratingText: 1,
        foreignKey: 1,
        integrationTestKey: 1,
        conversationPartId: 1,
        checkedByHuman: 1,
      });
    }, [conversationID, refreshTrigger]);

    const ratingEntitiesQuery = useQuery(
      () => getRatingsByTagList(selectedConversation.value?.odooTags || [])(selectionSet(() => RatingEntity, 1)),
      [selectedConversation.value?.odooTags?.join(',')],
    );

    const linkAndArticleNamesQuery = useQuery(
      () =>
        checkLinkAndArticleName(selectedConversation.value?.answer || '')(selectionSet(() => LinkAndArticleName, 2)),
      [selectedConversation.value?.answer],
    );

    let introspectionNode = useQuery(() => {
      if (!conversationID) return of(null);
      return getLastIntrospectNodeByOperationAndParam(
        'generateAnswerForMail',
        JSON.stringify({ answeringID: conversationID }),
      )({ _id: 1 });
    }, [conversationID]);

    if (!introspectionNode.value) {
      introspectionNode = useQuery(() => {
        if (!conversationID) return of(null);
        return getLastIntrospectNodeByOperationAndParam(
          'handleIntercomMode',
          JSON.stringify({ answeringID: conversationID }),
        )({ _id: 1 });
      }, [conversationID]);
    }

    const cleanupAnswerParseJsonAndHtml = (input: string | null | undefined) => {
      try {
        const parsed = JSON.parse(input ?? '');
        const html = parsed?.response_text;
        if (typeof html === 'string') {
          return unsafeHTML(html);
        }
      } catch (err) {
        try {
          const cleanedInput = unsafeHTML(input);
          return cleanedInput;
        } catch (err2) {
          return input;
        }
      }
    };

    // Handle comment submission
    const handleSubmitComment = async () => {
      if (!newComment.trim() || !conversationID) return;

      try {
        setIsSubmitting(true);
        setToastMessage(null);

        await addConversationComment(conversationID, newComment.trim(), 'answering')();
        setNewComment('');

        // Refresh the data by updating the refresh trigger
        setRefreshTrigger(refreshTrigger + 1);

        // Show success toast
        setToastMessage({ type: 'success', message: 'Comment added successfully!' });

        // Auto-dismiss toast after 3 seconds
        setTimeout(() => {
          setToastMessage(null);
        }, 3000);
      } catch (error) {
        console.error('Failed to add comment:', error);
        setToastMessage({ type: 'error', message: 'Failed to add comment' });
      } finally {
        setIsSubmitting(false);
      }
    };

    // Handle setting checked by human (for button)
    const handleSetCheckedByHuman = async () => {
      if (!conversationID) return;

      try {
        setIsSubmitting(true);
        setToastMessage(null);

        await setAnsweringCheckedByHuman(conversationID)();

        // Refresh the data by updating the refresh trigger
        setRefreshTrigger(refreshTrigger + 1);

        // Show success toast
        setToastMessage({ type: 'success', message: 'Marked as checked by human!' });

        // Auto-dismiss toast after 3 seconds
        setTimeout(() => {
          setToastMessage(null);
        }, 3000);
      } catch (error) {
        console.error('Failed to set checked by human:', error);
        setToastMessage({ type: 'error', message: 'Failed to mark as checked' });
      } finally {
        setIsSubmitting(false);
      }
    };

    // Handle checkbox toggle for checked by human
    const handleToggleCheckedByHuman = async () => {
      if (!conversationID) return;

      try {
        await setAnsweringCheckedByHuman(conversationID)();

        // Refresh the data by updating the refresh trigger
        setRefreshTrigger(refreshTrigger + 1);

        // Show success toast
        setToastMessage({ type: 'success', message: 'Updated checked by human status!' });

        // Auto-dismiss toast after 3 seconds
        setTimeout(() => {
          setToastMessage(null);
        }, 3000);
      } catch (error) {
        console.error('Failed to toggle checked by human:', error);
        setToastMessage({ type: 'error', message: 'Failed to update status' });
      }
    };

    // Render comments
    const renderComments = (comments: string[] | null | undefined) => {
      if (!comments || comments.length === 0) {
        return html`<div class="text-sm italic text-base-content/50 p-4 text-center">No comments yet</div>`;
      }

      return html`
        <div class="space-y-3">
          ${comments.map(
            (comment, index) => html`
              <div class="bg-base-200 p-3 rounded-lg">
                <div class="flex justify-between items-start">
                  <div class="text-sm whitespace-pre-wrap">${comment}</div>
                  <div class="text-xs text-base-content/50">#${index + 1}</div>
                </div>
              </div>
            `,
          )}
        </div>
      `;
    };

    if (!conversationID) return nothing;

    // Show loading state if conversation data is not yet loaded
    if (selectedConversation.loading) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body flex items-center justify-center">
            <span class="loading loading-spinner loading-lg text-secondary"></span>
          </div>
        </div>
      `;
    }

    // Show error state if there was an error loading the conversation
    if (selectedConversation.error) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-xl font-bold">Answering Details</h2>
              <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="flex flex-col items-center justify-center py-12 text-center">
              <div class="text-error mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <p class="text-error font-medium mb-2">Error loading conversation</p>
              <p class="text-base-content/70 text-sm">${selectedConversation.error.message}</p>
            </div>
          </div>
        </div>
      `;
    }

    // Show empty state if no conversation was found
    if (!selectedConversation.value) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-xl font-bold">Conversation Details</h2>
              <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="flex flex-col items-center justify-center py-12 text-center">
              <div class="text-base-content/50 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 16l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p class="font-medium mb-2">Conversation not found</p>
              <p class="text-base-content/70 text-sm">The requested conversation could not be found</p>
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
        <div class="card-body">
          <div class="flex justify-between items-center">
            <h2 class="card-title text-xl font-bold">Answering Details</h2>
            <div class="flex gap-2">
              ${
                !selectedConversation.value.checkedByHuman
                  ? html`
                      <button
                        class="btn btn-sm btn-primary"
                        @click=${handleSetCheckedByHuman}
                        ?disabled=${isSubmitting}
                        title="Mark as checked by human"
                      >
                        ${isSubmitting
                          ? html`<span class="loading loading-xs loading-spinner"></span>`
                          : 'Mark Checked'}
                      </button>
                    `
                  : html` <div class="badge badge-success badge-sm">Checked by Human</div> `
              }
              <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <p class="text-sm text-info">ID: ${selectedConversation.value._id}</p>
          <div class="divider"></div>

          <div class="space-y-4 overflow-y-auto max-h-[calc(100vh-300px)]">
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Created At</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${selectedConversation.value.createdAt ? format(selectedConversation.value.createdAt, 'PPpp') : 'N/A'}
              </div>
            </div>
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Foreign Key (Odoo or Intercom Ticket ID)</span>
                <div class="flex space-x-2 ml-auto"></div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${selectedConversation.value.foreignKey || 'N/A'}</div>
            </div>
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Foreign Key (Odoo or Intercom Conversation Part ID)</span>
                <div class="flex space-x-2 ml-auto"></div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${selectedConversation.value.conversationPartId || 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Question</span>
                <div class="flex space-x-2 ml-auto"></div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${selectedConversation.value.query || 'N/A'}</div>
            </div>
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Expected Answer</span>
                <div class="flex space-x-2 ml-auto"></div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${selectedConversation.value.expectedAnswer || 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Rating</span>
                <div class="flex space-x-2 ml-auto"></div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${selectedConversation.value.rating ?? 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Rating Text</span>
                <div class="flex space-x-2 ml-auto"></div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${selectedConversation.value.ratingText || 'N/A'}</div>
            </div>

            <!-- Handle checked by human -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Already Checked</span>
                <span class="label-text-alt">Is checked by human</span>
              </label>
              <div class="bg-base-200 rounded-lg p-3">
                <input
                  type="checkbox"
                  class="checkbox"
                  .checked=${selectedConversation.value.checkedByHuman}
                  @click=${(e: Event) => handleToggleCheckedByHuman()}
                />
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Odoo Tags</span>
                <div class="flex space-x-2 ml-auto"></div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${
                  selectedConversation.value.odooTags
                    ?.filter(obj => !obj.includes(':') && !obj.includes('my_data'))
                    .join(', ') || 'N/A'
                }
              </div>
            </div>

            <div class="form-control">
              <label class="label flex w-full justify-between">
                <span class="label-text font-medium">Answer</span>
                <div class="flex space-x-2 ml-auto">
                  ${
                    introspectionNode.value
                      ? html`
                          <button
                            class="btn btn-xs btn-outline"
                            @click=${() => setShowIntrospection(!showIntrospection)}
                          >
                            ${showIntrospection ? 'Hide Introspection' : 'Show Introspection'}
                          </button>
                        `
                      : nothing
                  }
                  <button class="btn btn-xs btn-secondary" @click=${() => alert('Coming soon...')}>
                    ${false ? 'Regenerating...' : 'Regenerate'}
                  </button>
                </div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
                ${cleanupAnswerParseJsonAndHtml(selectedConversation.value.answer) || 'N/A'}
              </div>
              <label class="label">
              <span class="label-text font-medium">Product data correctness check</span>
            </label>
            <div class="bg-base-200 p-3 rounded-lg text-sm">
                Correct products: ${linkAndArticleNamesQuery.value?.correctProducts?.length || 0} <br />
                Incorrect products: ${linkAndArticleNamesQuery.value?.incorrectProducts?.length || 0} <br />
                <strong>Correct Products:</strong> ${linkAndArticleNamesQuery.value?.correctProducts?.map(
                  (product: any) => html`<div class="badge-success">${JSON.stringify(product)}</div>`,
                )}<br />
                <strong>Incorrect Products:</strong> ${linkAndArticleNamesQuery.value?.incorrectProducts?.map(
                  (product: any) => html`<div class="badge-error">${product}</div>`,
                )}<br />
              </div>

               <label class="label">
              <span class="label-text font-medium">Accuracy by tag</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${
                  (ratingEntitiesQuery.value || []).length > 0
                    ? (ratingEntitiesQuery.value || []).map(
                        rating => html`
                          <div class="mb-2">
                            <span class="font-semibold">${rating.tag}:</span>
                            <span class="badge badge-info">${rating.rating || 'N/A'}</span>
                          </div>
                        `,
                      )
                    : html`<span class="text-base-content/50">No ratings available</span>`
                }

              </div>
            </label>

            </div>

            ${
              showIntrospection
                ? html`
                    <div class="modal modal-open">
                      <div class="modal-box w-full h-full p-0 relative max-w-full rounded-none">
                        ${IntrospectViewer({ nodeID: introspectionNode.value?._id })}
                        <button
                          class="btn btn-sm btn-circle absolute right-2 top-2"
                          @click=${() => setShowIntrospection(false)}
                          title="Close"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  `
                : nothing
            }

            <!-- Comments Section -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Comments</span>
                <span class="label-text-alt">${selectedConversation.value.comments?.length || 0} comments</span>
              </label>
              <div class="bg-base-200 rounded-lg">${renderComments(selectedConversation.value.comments)}</div>
            </div>

            <!-- Add Comment Form -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Add Comment</span>
              </label>
              <div class="relative">
                <textarea
                  class="textarea textarea-bordered w-full h-24 bg-base-200 text-sm resize-none"
                  placeholder="Add a comment..."
                  .value=${newComment}
                  @input=${(e: InputEvent) => setNewComment((e.target as HTMLTextAreaElement).value)}
                ></textarea>
                <div class="absolute bottom-3 right-3">
                  <button
                    class="btn btn-sm btn-primary"
                    @click=${handleSubmitComment}
                    ?disabled=${!newComment.trim() || isSubmitting}
                  >
                    ${
                      isSubmitting
                        ? html`<span class="loading loading-spinner loading-xs mr-1"></span> Saving...`
                        : 'Save'
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast notifications -->
      ${
        toastMessage
          ? html`
              <div class="toast toast-end">
                <div class="alert ${toastMessage.type === 'success' ? 'alert-success' : 'alert-error'} shadow-lg">
                  <div>
                    ${toastMessage.type === 'success'
                      ? html`
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        `
                      : html`
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        `}
                    <span>${toastMessage.message}</span>
                  </div>
                </div>
              </div>
            `
          : ''
      }
    `;
  },
);
